import { useState } from "react";
import { X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { adminApi } from "@/lib/api";
import type { AdminTeamSummary } from "@/lib/types";
import { cn, formatUSD } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";

function ModalShell({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full mx-4 sci-card p-6 max-h-[90vh] overflow-y-auto max-w-[480px]">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-base font-semibold text-white" style={{ fontFamily: "Space Grotesk, sans-serif" }}>
            {title}
          </h2>
          <button onClick={onClose} className="text-slate-500 hover:text-white">
            <X size={16} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

// VoucherRechargeModal adjusts team_wallets.voucher_balance via
// adminApi.rechargeVoucher (distinct from the cash-balance RechargeModal in
// AdminTeams.tsx). A single signed amount field accepts both positive
// (top-up) and negative (adjustment/recall) values — the backend rejects a
// zero amount, so that is validated client-side too.
export default function VoucherRechargeModal({
  team,
  onClose,
  onSaved,
}: {
  team: AdminTeamSummary;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useLanguage();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Generated once per modal instance so a duplicate submit (e.g. a slow
  // network causing an accidental double-click before `busy` disables the
  // button) doesn't double-apply on the backend.
  const [idempotencyKey] = useState(() =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `voucher-${team.team.id}-${Date.now()}-${Math.random()}`,
  );

  const submit = async () => {
    const raw = amount.trim();
    if (raw === "") {
      toast.error(t("billing.voucherAmountLabel") + " " + t("admin.common.required"));
      return;
    }
    const amt = Number(raw);
    if (!Number.isFinite(amt) || amt === 0) {
      toast.error(t("billing.voucherAmountInvalid"));
      return;
    }
    setBusy(true);
    try {
      const res = await adminApi.rechargeVoucher(
        team.team.id,
        { amount: raw, note: note.trim() || undefined },
        idempotencyKey,
      );
      toast.success(`${t("billing.voucherRechargedToast")} — ${formatUSD(res.voucher_balance)}`);
      onSaved();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Operation failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <ModalShell
      title={`${t("billing.modalVoucherRechargeTitle")} — ${team.team.name}`}
      onClose={onClose}
    >
      <div className="space-y-3">
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("billing.voucherAmountLabel")}
          </Label>
          <Input
            type="number"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            placeholder="e.g. 500 or -100"
            className="bg-white/5 border-white/10 text-white text-sm"
            autoFocus
          />
          <div className="text-[10px] text-slate-500 mt-1">
            {t("billing.currentVoucherBalance")}: {formatUSD(team.wallet?.voucher_balance ?? 0)}
          </div>
          <p className="text-[10px] text-amber-400/80 mt-1">
            {t("billing.voucherAmountHint")}
          </p>
        </div>
        <div>
          <Label className="text-xs text-slate-400 mb-1.5 block">
            {t("admin.teams.note")}
          </Label>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. quarterly promo credit"
            className="bg-white/5 border-white/10 text-white text-sm"
          />
        </div>
      </div>
      <div className="flex gap-3 pt-5">
        <Button variant="ghost" onClick={onClose} className="flex-1 text-slate-400 hover:text-white border border-white/10">
          {t("admin.common.cancel")}
        </Button>
        <Button onClick={submit} disabled={busy} className={cn("flex-1 btn-primary-sci")}>
          {busy ? "..." : t("billing.voucherRecharge")}
        </Button>
      </div>
    </ModalShell>
  );
}
